import { motion } from 'framer-motion';
import { MessageCircle, CheckCircle2, Factory, Truck, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const ProcessPage = () => {
  const steps = [
    {
      num: '01',
      title: 'Consultation',
      desc: 'We begin with a detailed consultation to understand your uniform requirements, brand identity, and specific needs.',
      icon: MessageCircle,
      details: [
        'Requirement gathering',
        'Fabric and design consultation',
        'Budget discussion',
        'Timeline planning',
      ],
    },
    {
      num: '02',
      title: 'Design & Sampling',
      desc: 'Our design team creates detailed specifications and physical samples for your approval before mass production.',
      icon: CheckCircle2,
      details: [
        'CAD design creation',
        'Material sourcing',
        'Sample production',
        'Revision cycles',
      ],
    },
    {
      num: '03',
      title: 'Manufacturing',
      desc: 'Using state-of-the-art facilities, we manufacture your uniforms with precision and attention to detail.',
      icon: Factory,
      details: [
        'Cutting and stitching',
        'Quality checkpoints',
        'Finishing and pressing',
        'Packaging preparation',
      ],
    },
    {
      num: '04',
      title: 'Quality Control',
      desc: 'Every piece undergoes rigorous quality checks to ensure it meets our high standards and your expectations.',
      icon: Award,
      details: [
        'Dimension verification',
        'Color consistency check',
        'Durability testing',
        'Final inspection',
      ],
    },
    {
      num: '05',
      title: 'Delivery & Support',
      desc: 'We ensure timely delivery and provide ongoing support for any adjustments or future orders.',
      icon: Truck,
      details: [
        'Secure packaging',
        'Logistics coordination',
        'On-time delivery',
        'After-sales support',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="process-page">
      {/* Hero */}
      <section className="section-spacing bg-brand-timberwolf" data-testid="process-hero">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-brand-pearl leading-none mb-6" data-testid="process-hero-heading">
              Our Manufacturing Process
            </h1>
            <p className="text-base sm:text-lg text-brand-walnut max-w-3xl mx-auto leading-relaxed">
              A proven 5-step process that combines traditional craftsmanship with modern manufacturing techniques to deliver quality uniforms at scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-spacing" data-testid="process-steps">
        <div className="container-custom">
          <div className="space-y-24">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6 }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`process-step-${idx}`}
                >
                  <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="relative">
                      <div className="absolute -top-8 -left-8 text-9xl font-heading font-bold text-brand-timberwolf opacity-30">
                        {step.num}
                      </div>
                      <div className="relative bg-brand-timberwolf rounded-sm p-12 border border-brand-pearl/10">
                        <div className="w-16 h-16 bg-brand-pearl rounded-full flex items-center justify-center mb-6">
                          <Icon className="w-8 h-8 text-brand-ivory" />
                        </div>
                        <h2 className="text-3xl font-heading font-bold text-brand-pearl mb-4">
                          {step.title}
                        </h2>
                        <p className="text-brand-walnut leading-relaxed mb-6">{step.desc}</p>
                        <ul className="space-y-2">
                          {step.details.map((detail, i) => (
                            <li key={i} className="flex items-center text-sm text-brand-walnut">
                              <CheckCircle2 className="w-4 h-4 mr-2 text-brand-green" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="parallax-img rounded-sm overflow-hidden border border-brand-pearl/10">
                      <img
                        src={`https://images.unsplash.com/photo-${['1451930264513-2e93da3ba755', '1758270804188-8ca0b6d254bc', '1684259499086-93cb3e555803', '1636986056375-184676d8ca14', '1758518730523-c9f6336ebdae'][idx]}?crop=entropy&cs=srgb&fm=jpg&q=85`}
                        alt={step.title}
                        className="w-full h-96 object-cover"
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Visual */}
      <section className="section-spacing bg-brand-pearl text-brand-ivory" data-testid="process-timeline">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
              Typical Project Timeline
            </h2>
            <p className="text-base sm:text-lg opacity-90 max-w-2xl mx-auto">
              From initial consultation to final delivery, here's what you can expect.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="flex items-center" data-testid="timeline-item-0">
                <div className="w-32 flex-shrink-0 font-mono text-sm">Days 1-3</div>
                <div className="flex-grow h-2 bg-brand-ivory/20 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ivory rounded-full" style={{ width: '15%' }} />
                </div>
                <div className="w-32 flex-shrink-0 text-right text-sm">Consultation</div>
              </div>
              <div className="flex items-center" data-testid="timeline-item-1">
                <div className="w-32 flex-shrink-0 font-mono text-sm">Days 4-10</div>
                <div className="flex-grow h-2 bg-brand-ivory/20 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ivory rounded-full" style={{ width: '30%' }} />
                </div>
                <div className="w-32 flex-shrink-0 text-right text-sm">Design & Sampling</div>
              </div>
              <div className="flex items-center" data-testid="timeline-item-2">
                <div className="w-32 flex-shrink-0 font-mono text-sm">Days 11-25</div>
                <div className="flex-grow h-2 bg-brand-ivory/20 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ivory rounded-full" style={{ width: '60%' }} />
                </div>
                <div className="w-32 flex-shrink-0 text-right text-sm">Manufacturing</div>
              </div>
              <div className="flex items-center" data-testid="timeline-item-3">
                <div className="w-32 flex-shrink-0 font-mono text-sm">Days 26-28</div>
                <div className="flex-grow h-2 bg-brand-ivory/20 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ivory rounded-full" style={{ width: '85%' }} />
                </div>
                <div className="w-32 flex-shrink-0 text-right text-sm">Quality Control</div>
              </div>
              <div className="flex items-center" data-testid="timeline-item-4">
                <div className="w-32 flex-shrink-0 font-mono text-sm">Days 29-30</div>
                <div className="flex-grow h-2 bg-brand-ivory/20 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ivory rounded-full" style={{ width: '100%' }} />
                </div>
                <div className="w-32 flex-shrink-0 text-right text-sm">Delivery</div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              data-testid="start-project-btn"
              className="inline-flex items-center px-8 py-3 bg-brand-ivory text-brand-pearl font-medium rounded-sm hover:bg-brand-timberwolf transition-colors"
            >
              Start Your Project
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;